import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  FileUp, IndianRupee, Eye, Trash2, Download, UserPlus, AlertTriangle, ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const inr = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const norm = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]+/g, " ").trim();

export default function DomesticSales({ permissions = [] }) {
  const has = (p) => permissions.includes(p);
  const canUpload = has("packing_list.upload");
  const canDelete = has("packing_list.delete");
  const canManageCust = has("customer.manage");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const fileRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [linesDialog, setLinesDialog] = useState({ open: false, row: null, data: null, loading: false });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null });
  const [addCust, setAddCust] = useState({ open: false, name: "", gstin: "", busy: false });

  const custNames = useMemo(() => {
    const set = new Set();
    for (const c of customers) {
      if (c.name) set.add(norm(c.name));
      for (const a of c.aliases || []) set.add(norm(a));
    }
    return set;
  }, [customers]);

  const isMatched = (row) => custNames.has(norm(row.customer_name));

  const fetchList = async () => {
    try {
      const r = await axios.get(`${API}/packing-lists?sales_type=domestic`, authHeaders());
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load domestic invoices");
    } finally { setLoading(false); }
  };
  const fetchCustomers = async () => {
    try { const r = await axios.get(`${API}/customers`, authHeaders()); setCustomers(r.data || []); } catch (_) {}
  };

  useEffect(() => { fetchList(); fetchCustomers(); /* eslint-disable-next-line */ }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = [".xls", ".xlsx", ".xlsm"].some((x) => file.name.toLowerCase().endsWith(x));
    if (!ok) { toast.error("Domestic invoices must be Excel (.xls/.xlsx)"); if (fileRef.current) fileRef.current.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sales_type", "domestic");
      if (note.trim()) fd.append("note", note.trim());
      const r = await axios.post(`${API}/packing-lists/upload`, fd, {
        headers: { ...authHeaders().headers, "Content-Type": "multipart/form-data" },
      });
      const d = r.data;
      if (d.sales_type_mismatch) {
        toast.warning(`Uploaded, but this looks like an EXPORT document — check the Export tab if that's wrong.`, { duration: 7000 });
      } else if (d.invoice_no) {
        toast.success(`Uploaded · ${d.invoice_no} · ${inr(d.taxable_value)} taxable`);
      } else {
        toast.success("Uploaded — could not auto-read invoice fields.");
      }
      setNote("");
      if (fileRef.current) fileRef.current.value = "";
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const viewLines = async (row) => {
    setLinesDialog({ open: true, row, data: null, loading: true });
    try {
      const r = await axios.get(`${API}/packing-lists/${row.id}/domestic-lines`, authHeaders());
      setLinesDialog({ open: true, row, data: r.data, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoice");
      setLinesDialog({ open: false, row: null, data: null, loading: false });
    }
  };

  const download = async (row) => {
    try {
      const res = await axios.get(`${API}/packing-lists/${row.id}/download`, authHeaders());
      const url = res.data?.url;
      if (!url) { window.open(`${API}/packing-lists/${row.id}/download`, "_blank"); return; }
      const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.rel = "noopener";
      a.download = row.file_name || "invoice"; document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  const doDelete = async () => {
    if (!deleteDialog.row) return;
    try {
      await axios.delete(`${API}/packing-lists/${deleteDialog.row.id}`, authHeaders());
      toast.success("Deleted");
      setDeleteDialog({ open: false, row: null });
      await fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const openAddCust = (row) => setAddCust({ open: true, name: row.customer_name || "", gstin: row.buyer_gstin || "", busy: false });
  const submitAddCust = async () => {
    const name = (addCust.name || "").trim();
    if (!name) { toast.error("Customer name required"); return; }
    setAddCust((s) => ({ ...s, busy: true }));
    try {
      await axios.post(`${API}/customers`, {
        name,
        aliases: [],
        notes: addCust.gstin ? `GSTIN: ${addCust.gstin}` : null,
      }, authHeaders());
      toast.success(`${name} added — set its vertical in Customer Master`);
      setAddCust({ open: false, name: "", gstin: "", busy: false });
      await fetchCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add customer");
      setAddCust((s) => ({ ...s, busy: false }));
    }
  };

  const unmatched = items.filter((r) => r.customer_name && !isMatched(r));

  return (
    <div data-testid="domestic-sales-panel">
      {/* Upload */}
      {canUpload && (
        <div className="sf-surface p-4 mb-4" data-testid="dom-upload-card">
          <div className="flex items-center gap-2 mb-3">
            <FileUp className="w-4 h-4 text-emerald-600" />
            <span className="font-medium text-sm">Upload Domestic Tax Invoice (INR)</span>
          </div>
          <div className="grid md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <Label className="text-xs text-zinc-500">Optional note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. ModulArt project ref" className="mt-1" data-testid="dom-note-input" />
            </div>
            <div>
              <input ref={fileRef} type="file" accept=".xls,.xlsx,.xlsm" onChange={handleFile} className="hidden" data-testid="dom-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dom-upload-btn">
                <FileUp className="w-4 h-4 mr-1.5" />{uploading ? "Uploading…" : "Upload Invoice"}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">GST tax invoices in INR (CGST/SGST). Figures feed the Management Report by the customer's vertical.</p>
        </div>
      )}

      {/* Unmatched customers banner */}
      {unmatched.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="dom-unmatched-banner">
          <div className="flex items-center gap-2 text-sm text-amber-800 font-medium mb-1.5">
            <AlertTriangle className="w-4 h-4" />
            {unmatched.length} invoice{unmatched.length > 1 ? "s" : ""} from customer{unmatched.length > 1 ? "s" : ""} not yet in the Customer Master
          </div>
          <p className="text-xs text-amber-700">These won't roll up to a vertical until the customer is added and classified. Use "Add to master" on the row, then set the vertical in Customer Master.</p>
        </div>
      )}

      {/* List */}
      <div className="sf-surface p-4">
        <div className="flex items-center gap-2 mb-3">
          <IndianRupee className="w-4 h-4 text-emerald-600" />
          <span className="font-medium text-sm">Domestic Sales Invoices</span>
          <span className="text-xs text-zinc-500">({items.length})</span>
        </div>
        <div className="overflow-x-auto rounded border bg-white">
          {loading ? (
            <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-zinc-500">No domestic invoices yet. Upload one above.</div>
          ) : (
            <table className="w-full text-sm" data-testid="dom-invoice-table">
              <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                <tr>
                  <th className="p-3 text-left">Invoice</th>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Customer</th>
                  <th className="p-3 text-right">Taxable</th>
                  <th className="p-3 text-right">GST</th>
                  <th className="p-3 text-right">Total</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((row) => {
                  const gst = (row.cgst || 0) + (row.sgst || 0) + (row.igst || 0);
                  const matched = isMatched(row);
                  return (
                    <tr key={row.id} className="hover:bg-zinc-50" data-testid={`dom-row-${row.id}`}>
                      <td className="p-3 font-mono text-xs">{row.invoice_no || row.file_name}</td>
                      <td className="p-3 text-xs text-zinc-600">{row.invoice_date || "—"}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate max-w-[200px]" title={row.customer_name}>{row.customer_name || "—"}</span>
                          {row.customer_name && !matched && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid={`dom-unmatched-tag-${row.id}`}>new</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right font-medium">{inr(row.taxable_value)}</td>
                      <td className="p-3 text-right text-xs text-zinc-500">{inr(gst)}</td>
                      <td className="p-3 text-right font-semibold text-emerald-700">{inr(row.total_value)}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => viewLines(row)} data-testid={`dom-view-${row.id}`} title="View invoice"><Eye className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => download(row)} data-testid={`dom-download-${row.id}`} title="Download original"><Download className="w-4 h-4" /></Button>
                          {canManageCust && row.customer_name && !matched && (
                            <Button variant="outline" size="sm" className="h-7 px-2 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-50" onClick={() => openAddCust(row)} data-testid={`dom-addcust-${row.id}`}><UserPlus className="w-3.5 h-3.5 mr-1" />Add to master</Button>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500" onClick={() => setDeleteDialog({ open: true, row })} data-testid={`dom-delete-${row.id}`}><Trash2 className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Invoice viewer popup */}
      <Dialog open={linesDialog.open} onOpenChange={(o) => !o && setLinesDialog({ open: false, row: null, data: null, loading: false })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="dom-lines-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><IndianRupee className="w-5 h-5 text-emerald-600" /> {linesDialog.data?.invoice_no || linesDialog.row?.invoice_no || "Invoice"}</DialogTitle>
            <DialogDescription>
              {linesDialog.data?.customer_name}{linesDialog.data?.buyer_gstin ? ` · GSTIN ${linesDialog.data.buyer_gstin}` : ""}
              {linesDialog.data?.po_number ? ` · PO ${linesDialog.data.po_number}` : ""}
              {linesDialog.data?.invoice_date ? ` · ${linesDialog.data.invoice_date}` : ""}
            </DialogDescription>
          </DialogHeader>
          {linesDialog.loading ? (
            <div className="py-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>
          ) : linesDialog.data ? (
            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm mb-4" data-testid="dom-lines-table">
                <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase sticky top-0">
                  <tr>
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Description</th>
                    <th className="p-2 text-left">HSN</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-right">Rate</th>
                    <th className="p-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(linesDialog.data.items || []).map((it) => (
                    <tr key={it.id}>
                      <td className="p-2 text-zinc-400">{it.line_no}</td>
                      <td className="p-2">{it.description}</td>
                      <td className="p-2 font-mono text-xs">{it.hsn || "—"}</td>
                      <td className="p-2 text-right">{it.qty} {it.unit || ""}</td>
                      <td className="p-2 text-right">{inr(it.rate)}</td>
                      <td className="p-2 text-right font-medium">{inr(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="ml-auto max-w-xs text-sm space-y-1" data-testid="dom-totals">
                <div className="flex justify-between"><span className="text-zinc-500">Taxable value</span><span className="font-medium">{inr(linesDialog.data.totals?.taxable_value)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">CGST</span><span>{inr(linesDialog.data.totals?.cgst)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">SGST</span><span>{inr(linesDialog.data.totals?.sgst)}</span></div>
                {(linesDialog.data.totals?.igst || 0) > 0 && <div className="flex justify-between"><span className="text-zinc-500">IGST</span><span>{inr(linesDialog.data.totals?.igst)}</span></div>}
                {(linesDialog.data.totals?.round_off || 0) !== 0 && <div className="flex justify-between"><span className="text-zinc-500">Round off</span><span>{inr(linesDialog.data.totals?.round_off)}</span></div>}
                <div className="flex justify-between border-t pt-1 font-bold text-emerald-700"><span>Total</span><span data-testid="dom-total-value">{inr(linesDialog.data.totals?.total_value)}</span></div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => linesDialog.row && download(linesDialog.row)}><ExternalLink className="w-4 h-4 mr-1.5" />View original file</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add customer dialog */}
      <Dialog open={addCust.open} onOpenChange={(o) => !o && !addCust.busy && setAddCust({ open: false, name: "", gstin: "", busy: false })}>
        <DialogContent data-testid="dom-addcust-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-600" /> Add customer to master</DialogTitle>
            <DialogDescription>Creates the customer. Assign its vertical (e.g. ModulArt projects) in Customer Master afterwards.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-zinc-500">Customer name</Label><Input value={addCust.name} onChange={(e) => setAddCust((s) => ({ ...s, name: e.target.value }))} className="mt-1" data-testid="dom-addcust-name" /></div>
            <div><Label className="text-xs text-zinc-500">GSTIN (stored in notes)</Label><Input value={addCust.gstin} onChange={(e) => setAddCust((s) => ({ ...s, gstin: e.target.value }))} className="mt-1 font-mono" data-testid="dom-addcust-gstin" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCust({ open: false, name: "", gstin: "", busy: false })} disabled={addCust.busy}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitAddCust} disabled={addCust.busy} data-testid="dom-addcust-submit">{addCust.busy ? "Adding…" : "Add customer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, row: null })}>
        <DialogContent data-testid="dom-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" /> Delete invoice?</DialogTitle>
            <DialogDescription>{deleteDialog.row?.invoice_no || deleteDialog.row?.file_name}<br />This removes it from domestic sales and the Management Report.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, row: null })}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="dom-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
